import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { workspaceApi, type ActivityEntry, type AuditEntry, type Dashboard, type SettingsBundle, type Workspace } from '../../api/workspaces';
import { WorkspaceSelector } from '../../components/WorkspaceSelector';

type Tab = 'overview' | 'dashboard' | 'analytics' | 'settings' | 'branding' | 'activity' | 'audit';

export function WorkspaceDetailPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab | null) ?? 'overview';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [ws, setWs] = useState<Workspace | null>(null);
  const [settings, setSettings] = useState<SettingsBundle | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState('');

  useEffect(() => { void workspaceApi.get(id).then(setWs).catch((e) => setError(String(e))); }, [id]);
  useEffect(() => {
    if (!id) return;
    if (tab === 'settings') void workspaceApi.getSettings(id).then(setSettings);
    if (tab === 'dashboard') void workspaceApi.getDashboard(id).then(setDashboard);
    if (tab === 'activity') void workspaceApi.getActivity(id).then(setActivity);
    if (tab === 'audit') void workspaceApi.getAudit(id).then(setAudit);
  }, [id, tab]);

  if (!ws && !error) return <p className="p-6">Loading…</p>;
  if (!ws) return <p className="p-6 text-red-600">{error}</p>;

  const tabs: Tab[] = ['overview', 'dashboard', 'analytics', 'settings', 'branding', 'activity', 'audit'];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Link to="/workspaces" className="text-sm text-slate-600 hover:underline">← Workspaces</Link>
      <div className="mt-4 flex flex-wrap justify-between gap-4">
        <div><h1 className="text-2xl font-semibold">{ws.name}</h1><p className="text-sm text-slate-600">{ws.slug} · {ws.status}</p>
          <Link to={`/workspaces/${id}/dashboard`} className="mt-1 inline-block text-sm text-slate-700 hover:underline">Open dashboard</Link>
        </div>
        <div className="flex gap-2">
          {ws.status === 'pending' && <button type="button" onClick={() => void workspaceApi.activate(id).then(setWs)} className="rounded-lg border px-3 py-1.5 text-sm">Activate</button>}
          {ws.status === 'active' && <button type="button" onClick={() => void workspaceApi.switch(id)} className="rounded-lg border border-blue-300 px-3 py-1.5 text-sm text-blue-800">Switch</button>}
        </div>
      </div>
      <div className="mt-4 max-w-sm"><WorkspaceSelector currentId={ws.id} /></div>
      <nav className="mt-6 flex gap-1 border-b">
        {tabs.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} className={`px-4 py-2 text-sm capitalize ${tab === t ? 'border-b-2 border-slate-900 font-medium' : 'text-slate-600'}`}>{t}</button>
        ))}
      </nav>
      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid gap-4 md:grid-cols-2 rounded-xl border bg-white p-4 text-sm">
            <p>ID: {ws.id}</p><p>Tenant: {ws.tenant_id}</p><p>Org: {ws.organization_id}</p>
            <p>Timezone: {ws.timezone || ws.profile?.timezone || 'UTC'}</p><p>Type: {ws.type || 'general'}</p>
          </div>
        )}
        {tab === 'dashboard' && dashboard && (
          <div className="rounded-xl border bg-white p-4">
            <h2 className="font-medium">Dashboard</h2>
            <pre className="mt-2 overflow-auto text-xs text-slate-600">{JSON.stringify(dashboard.stats, null, 2)}</pre>
            {dashboard.widgets?.length === 0 && <p className="mt-2 text-sm text-slate-500">No widgets configured.</p>}
          </div>
        )}
        {tab === 'analytics' && (
          <div className="rounded-xl border bg-white p-4">
            <p className="text-sm text-slate-600">View full analytics on the dedicated page.</p>
            <Link to={`/workspaces/${id}/analytics`} className="mt-2 inline-block text-sm text-slate-900 hover:underline">Open analytics</Link>
          </div>
        )}
        {tab === 'settings' && settings && (
          <div className="rounded-xl border bg-white p-4 text-sm">
            <p>Theme: {settings.settings?.theme || '—'}</p>
            <p className="mt-2">Notifications: {settings.preferences?.notifications_enabled ? 'On' : 'Off'}</p>
          </div>
        )}
        {tab === 'branding' && (
          <div className="rounded-xl border bg-white p-4 text-sm">
            <p>Display: {ws.branding?.display_name || ws.name}</p>
            <p>Primary: {ws.branding?.primary_color || '—'}</p>
          </div>
        )}
        {tab === 'activity' && (
          <ul className="space-y-2">{activity.map((a) => (
            <li key={a.id} className="rounded-lg border p-3 text-sm"><p className="font-medium">{a.summary}</p><p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</p></li>
          ))}</ul>
        )}
        {tab === 'audit' && (
          <ul className="space-y-2">{audit.map((a) => (
            <li key={a.id} className="rounded-lg border p-3 text-sm"><p>{a.action} — {a.outcome}</p></li>
          ))}</ul>
        )}
      </div>
    </div>
  );
}
